/// <reference path="../../../../node_modules/monaco-editor-core/monaco.d.ts" />
import { Directive, inject, Input } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputObservable } from '@y42/angular/util';
import { UserColorSchemeService } from '@y42/browser/user-color-scheme';
import { Plugin } from 'prettier';
import { merge, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { MonacoEditorBase, MONACO_SETTINGS } from './monaco-editor-base';
import { addPrettierFormatting, monacoColorThemes } from './monaco.helper';
import { sqlDarkTheme } from './themes/sql-dark-theme';
import { sqlLightTheme } from './themes/sql-light-theme';

export enum LanguageType {
  Sql = 'sql',
}

@UntilDestroy()
@Directive({ selector: '[y42MonacoEditor]' })
export class MonacoEditorDirective extends MonacoEditorBase<monaco.editor.IStandaloneCodeEditor> {
  constructor() {
    super();

    merge(
      this.updateOptionsOnOptionsChange(),
      this.setFormControlValueOnContentChange(),
      this.selectTextOnSelectionRangeChange(),
    )
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  @Input()
  @InputObservable()
  options: monaco.editor.IStandaloneEditorConstructionOptions = MONACO_SETTINGS;
  private readonly options$!: Observable<monaco.editor.IStandaloneEditorConstructionOptions>;

  @Input()
  @InputObservable()
  content: string | null | undefined = null;
  private readonly content$!: Observable<string | null | undefined>;

  @Input()
  @InputObservable()
  selectionRange: monaco.Range | null = null;
  private readonly selectionRange$!: Observable<monaco.Range | null>;

  @Input()
  formattingLanguages: LanguageType[] = [];

  @Input()
  expandSuggestionsDocsByDefault = false;

  private decorations: string[] = [];
  protected formatCommandId = '';

  private readonly userColorSchemeService = inject(UserColorSchemeService);

  private updateOptionsOnOptionsChange() {
    return this.options$.pipe(tap((options) => this.editor?.updateOptions({ ...MONACO_SETTINGS, ...options })));
  }

  private setFormControlValueOnContentChange() {
    return this.content$.pipe(
      tap((content) => {
        if (!this.editor) {
          return;
        }
        if (this.editor.getValue() !== content) {
          this.editor.setValue(content || '');
        }
      }),
    );
  }

  private selectTextOnSelectionRangeChange() {
    return this.selectionRange$.pipe(tap(() => this.selectText()));
  }

  async init(): Promise<void> {
    if (this.fileName) {
      this.options.language = this.languageId;
    }

    this.editor = monaco.editor.create(
      this.host.nativeElement,
      {
        ...MONACO_SETTINGS,
        ...this.options,
        value: this.content || '',
      },
      this.expandSuggestionsDocsByDefault
        ? {
            storageService: {
              get() {
                // Empty.
              },
              getBoolean(key: string) {
                return key === 'expandSuggestionDocs';
              },
              remove() {
                // Empty.
              },
              store() {
                // Empty.
              },
              onWillSaveState() {
                // Empty.
              },
              onDidChangeStorage() {
                // Empty.
              },
            },
          }
        : {},
    );

    this.selectText();
    this.defineThemes();

    this.toggleDarkModeOnUserColorSchemeChange().subscribe();

    this.editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KEY_Y, () => {
      this.redo();
    });

    this.editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Shift | monaco.KeyCode.KEY_F, () => {
      this.format();
    });

    if (this.formattingLanguages.length) {
      this.applyFormatters();
    }
  }

  format() {
    if (!this.formatCommandId) {
      return;
    }

    this.editor?.trigger('', this.formatCommandId, null);
  }

  undo() {
    this.editor?.trigger('', 'undo', null);
  }

  redo() {
    this.editor?.trigger('', 'redo', '');
  }

  private async getPlugin(languageType: LanguageType): Promise<Plugin<string>> {
    switch (languageType) {
      case LanguageType.Sql:
        const { SqlPlugin } = await import('@y42/sql-prettier');
        return SqlPlugin;
      default:
        throw new Error('This language type is not supported');
    }
  }

  private async applyFormatters() {
    const plugins = await Promise.all(this.formattingLanguages.map((language) => this.getPlugin(language)));

    this.formatCommandId = addPrettierFormatting(this.editor, [], plugins);
  }

  private selectText() {
    if (!this.editor || !this.selectionRange) {
      return;
    }

    this.decorations = this.editor.deltaDecorations(this.decorations, [
      {
        range: this.selectionRange,
        options: {
          className: 'bg-primary-100',
        },
      },
    ]);
    this.editor.revealRangeInCenter(this.selectionRange, monaco.editor.ScrollType.Immediate);
  }

  private defineThemes() {
    monaco.editor.defineTheme(monacoColorThemes.sql.light, sqlLightTheme);
    monaco.editor.defineTheme(monacoColorThemes.sql.dark, sqlDarkTheme);
  }

  setTheme(theme: string) {
    if (!this.editor) {
      return;
    }
    this.editor.updateOptions({ theme });
  }

  private toggleDarkModeOnUserColorSchemeChange(): Observable<boolean> {
    return this.userColorSchemeService.hasDarkMode$.pipe(
      tap((hasDarkMode) => {
        this.setTheme(hasDarkMode ? monacoColorThemes.sql.dark : monacoColorThemes.sql.light);
      }),
      untilDestroyed(this),
    );
  }
}
